import i18n from "../i18n";
import {
  traditionalChineseDialogPatterns,
  traditionalChineseDialogText,
} from "../locales/dialogTranslations";
import { buttonText } from "./buttonText";

const DIALOG_SELECTOR = '.modal, [role="dialog"], .ReactModal__Content';
const LOCALIZED_ATTRIBUTES = ["aria-label", "placeholder", "title"];
const installedDocuments = new WeakSet();
const localizedTextNodes = new Map();
const localizedAttributes = new Map();

export function dialogText(value) {
  if (typeof value !== "string") return value;
  if ((i18n.resolvedLanguage || i18n.language) !== "zh-TW") return value;

  const directTranslation = traditionalChineseDialogText[value];
  if (directTranslation) return directTranslation;

  const pattern = traditionalChineseDialogPatterns.find(([source]) =>
    source.test(value)
  );
  if (pattern) return value.replace(pattern[0], pattern[1]);

  return buttonText(value);
}

function replacePreservingWhitespace(value) {
  const content = value.trim();
  if (!content) return value;
  const translation = dialogText(content);
  if (translation === content) return value;
  const leadingWhitespace = value.match(/^\s*/)?.[0] || "";
  const trailingWhitespace = value.match(/\s*$/)?.[0] || "";
  return `${leadingWhitespace}${translation}${trailingWhitespace}`;
}

function localizeTextNode(node) {
  const currentValue = node.nodeValue || "";
  const previous = localizedTextNodes.get(node);
  if (previous?.translation === currentValue) return;
  if (previous && previous.translation !== currentValue) {
    localizedTextNodes.delete(node);
  }

  const translation = replacePreservingWhitespace(currentValue);
  if (translation === currentValue) return;
  localizedTextNodes.set(node, { source: currentValue, translation });
  node.nodeValue = translation;
}

function localizeAttribute(element, attributeName) {
  const currentValue = element.getAttribute(attributeName);
  if (!currentValue) return;

  let tracked = localizedAttributes.get(element);
  const previous = tracked?.get(attributeName);
  if (previous?.translation === currentValue) return;
  if (previous && previous.translation !== currentValue) {
    tracked.delete(attributeName);
  }

  const translation = dialogText(currentValue);
  if (translation === currentValue) return;
  if (!tracked) {
    tracked = new Map();
    localizedAttributes.set(element, tracked);
  }
  tracked.set(attributeName, { source: currentValue, translation });
  element.setAttribute(attributeName, translation);
}

function localizeDialogTree(root, ownerDocument) {
  if (!root) return;
  const element = root.nodeType === 1 ? root : root.parentElement;
  if (!element) return;

  const dialogRoot = element.matches?.(DIALOG_SELECTOR)
    ? element
    : element.closest?.(DIALOG_SELECTOR);
  if (!dialogRoot) return;

  const treeWalker = ownerDocument.createTreeWalker(
    root.nodeType === 3 ? root.parentElement : root,
    ownerDocument.defaultView.NodeFilter.SHOW_TEXT
  );
  let textNode = treeWalker.nextNode();
  while (textNode) {
    localizeTextNode(textNode);
    textNode = treeWalker.nextNode();
  }

  const elements = root.nodeType === 1
    ? [root, ...root.querySelectorAll("*")]
    : [root.parentElement];
  elements.forEach((currentElement) => {
    LOCALIZED_ATTRIBUTES.forEach((attributeName) =>
      localizeAttribute(currentElement, attributeName)
    );
  });
}

function localizeAddedTree(root, ownerDocument) {
  if (root.nodeType === 3) {
    if (root.parentElement?.closest(DIALOG_SELECTOR)) {
      localizeTextNode(root);
    }
    return;
  }
  if (root.nodeType !== 1) return;

  if (root.matches(DIALOG_SELECTOR) || root.closest(DIALOG_SELECTOR)) {
    localizeDialogTree(root, ownerDocument);
  }
  root.querySelectorAll(DIALOG_SELECTOR).forEach((dialog) =>
    localizeDialogTree(dialog, ownerDocument)
  );
}

function restoreEnglish() {
  localizedTextNodes.forEach(({ source, translation }, node) => {
    if (node.nodeValue === translation) node.nodeValue = source;
  });
  localizedTextNodes.clear();

  localizedAttributes.forEach((attributes, element) => {
    attributes.forEach(({ source, translation }, attributeName) => {
      if (element.getAttribute(attributeName) === translation) {
        element.setAttribute(attributeName, source);
      }
    });
  });
  localizedAttributes.clear();
}

export function installLocalizedModalContent(ownerDocument = document) {
  if (!ownerDocument?.body || installedDocuments.has(ownerDocument)) return;
  installedDocuments.add(ownerDocument);

  const localizeOpenDialogs = () => {
    if ((i18n.resolvedLanguage || i18n.language) !== "zh-TW") return;
    ownerDocument.querySelectorAll(DIALOG_SELECTOR).forEach((dialog) =>
      localizeDialogTree(dialog, ownerDocument)
    );
  };

  const observer = new ownerDocument.defaultView.MutationObserver((mutations) => {
    if ((i18n.resolvedLanguage || i18n.language) !== "zh-TW") return;
    mutations.forEach((mutation) => {
      if (mutation.type === "attributes") {
        if (mutation.target.closest(DIALOG_SELECTOR)) {
          localizeAttribute(mutation.target, mutation.attributeName);
        }
        return;
      }
      if (mutation.type === "characterData") {
        if (mutation.target.parentElement?.closest(DIALOG_SELECTOR)) {
          localizeTextNode(mutation.target);
        }
        return;
      }
      mutation.addedNodes.forEach((node) =>
        localizeAddedTree(node, ownerDocument)
      );
    });
  });

  observer.observe(ownerDocument.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: LOCALIZED_ATTRIBUTES,
  });

  i18n.on("languageChanged", (language) => {
    if (language === "zh-TW") localizeOpenDialogs();
    else restoreEnglish();
  });
  localizeOpenDialogs();
}
